import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  useMediaQuery,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import { CameraAlt, Upload } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { inputSetScore, saveScorecardPhoto } from '../../features/game/gameSlice';
import { useTheme } from '@mui/material/styles';
import { prepareScorecardPhoto } from '../../helpers/imageResize';

const ScoreInput = ({
  teamName,
  control,
  name,
  isMobile,
  showLabel = true,
  compact = false,
  maxValue = 999,
  allowDecimal = false,
  allowNegative = false,
  onValueChange
}) => {
  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignContent={'space-between'}
      textAlign={'center'}>
      {showLabel && (
        <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" sx={{ mb: 1 }}>
          {teamName}
        </Typography>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            variant="outlined"
            type={allowDecimal ? 'text' : 'number'}
            inputProps={
              allowDecimal
                ? {
                    // iOS ignores inputmode="decimal" and gives its plain
                    // keyboard, so there is no decimal key to press. A pattern
                    // is what makes iOS raise a numeric pad on a text input.
                    // It only has to let every value the filter below allows
                    // through - the filter is what actually constrains it.
                    inputMode: 'decimal',
                    pattern: '[0-9.-]*',
                    min: allowNegative ? undefined : 0,
                    max: maxValue
                  }
                : { min: 0, max: maxValue }
            }
            autoComplete="off"
            onChange={(event) => {
              // An iPhone whose locale uses a comma sends ',' from the decimal
              // key, which the filter would otherwise reject outright.
              const nextValue = event.target.value.replace(/,/g, '.');
              const valid = allowDecimal
                ? allowNegative
                  ? /^-?\d*\.?\d*$/.test(nextValue)
                  : /^\d*\.?\d*$/.test(nextValue)
                : /^\d+$/.test(nextValue);

              if (nextValue === '' || valid) {
                if (onValueChange) {
                  onValueChange(nextValue, field);
                } else {
                  field.onChange(nextValue);
                }
              }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'center',
                fontSize: compact
                  ? isMobile
                    ? '1.35rem'
                    : '1.9rem'
                  : isMobile
                    ? '1.75rem'
                    : '2.5rem',
                padding: compact ? (isMobile ? '8px' : '10px') : isMobile ? '10px' : '14px',
                fontWeight: 'bold'
              }
            }}
            placeholder="0"
            fullWidth
          />
        )}
      />
    </Box>
  );
};

const InputScoreDialog = ({
  game,
  onOpen,
  submitScore,
  savePhoto = null,
  submitting = false,
  allowPhotos = true,
  triggerColor = 'secondary',
  triggerSx = null
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const inputtingSetScore = useSelector((state) => state.game.inputtingSetScore);
  const busy = submitting || inputtingSetScore;

  const [open, setOpen] = useState(false);
  const [scorecardPhoto, setScorecardPhoto] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [needsTiebreaker, setNeedsTiebreaker] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const processingPhotoRef = useRef(false);

  const totalSets = game.gameFormat.sets;
  const hasExtraPoints = game.gameFormat.extraEndGamePoint > 0;
  const setLabel = game.gameFormat?.setAsSkin ? 'Skin' : 'Set';
  const setLabelPlural = game.gameFormat?.setAsSkin ? 'Skins' : 'Sets';

  const teamA = game.teams.teamA;
  const teamB = game.teams.teamB;

  const getInitialFormValues = () => {
    const values = {};

    for (let i = 0; i < totalSets; i++) {
      values[`scoreA_${i}`] = '';
      values[`scoreB_${i}`] = '';
    }

    values.tiebreakerA = '';
    values.tiebreakerB = '';
    values.extraEndA = '0';
    values.extraEndB = '0';

    return values;
  };

  const {
    control,
    setValue,
    watch,
    reset,
    handleSubmit: rhfHandleSubmit
  } = useForm({
    mode: 'onChange',
    defaultValues: getInitialFormValues()
  });

  const allValues = watch();
  // The extra-end points cap is normally ends x sets x point-per-end. When
  // ends is 0 (meaning "unlimited ends", see helper text in the game format
  // override form) that multiplication collapses the cap to 0, so this field
  // could never be filled in. Only fall back to a sets-only cap in that
  // unlimited-ends case; otherwise use the real ends count like before.
  const endsPerSet = Number(game.gameFormat?.ends || 0);
  const extraEndPointValue = Number(game.gameFormat?.extraEndGamePoint || 0);
  const maxExtraEndPointsTotal =
    (endsPerSet > 0 ? endsPerSet : 1) * Math.max(Number(totalSets || 0), 1) * extraEndPointValue;
  const drawPoints =
    game.gameFormat?.drawPoints !== undefined && game.gameFormat?.drawPoints !== null
      ? Number(game.gameFormat.drawPoints)
      : Number(game.gameFormat?.winPoints || 0) / 2;

  const setPointPreview = Array.from({ length: totalSets }).reduce(
    (acc, _, index) => {
      const scoreA = parseInt(allValues[`scoreA_${index}`], 10) || 0;
      const scoreB = parseInt(allValues[`scoreB_${index}`], 10) || 0;

      if (scoreA === 0 && scoreB === 0) {
        return acc;
      }

      if (scoreA > scoreB) {
        acc.teamASetWins += 1;
        acc.teamASetPoints += Number(game.gameFormat?.maxTotalPerSet || 0);
      } else if (scoreB > scoreA) {
        acc.teamBSetWins += 1;
        acc.teamBSetPoints += Number(game.gameFormat?.maxTotalPerSet || 0);
      } else {
        acc.drawnSets += 1;
        acc.teamASetPoints += Number(game.gameFormat?.maxTotalPerSet || 0) / 2;
        acc.teamBSetPoints += Number(game.gameFormat?.maxTotalPerSet || 0) / 2;
      }

      acc.teamAShots += scoreA;
      acc.teamBShots += scoreB;
      return acc;
    },
    {
      teamASetPoints: 0,
      teamBSetPoints: 0,
      teamASetWins: 0,
      teamBSetWins: 0,
      drawnSets: 0,
      teamAShots: 0,
      teamBShots: 0
    }
  );

  const teamAExtraPreview = parseFloat(allValues.extraEndA || 0) || 0;
  const teamBExtraPreview = parseFloat(allValues.extraEndB || 0) || 0;
  const includeSetPointsInGameTotals = !game.gameFormat?.excludeSetPointInGamePoints;

  let teamAWinPointsPreview = 0;
  let teamBWinPointsPreview = 0;

  if (game.gameFormat?.playoffs && needsTiebreaker) {
    const tiebreakerA = parseInt(allValues.tiebreakerA, 10) || 0;
    const tiebreakerB = parseInt(allValues.tiebreakerB, 10) || 0;

    if (tiebreakerA > tiebreakerB) {
      teamAWinPointsPreview = Number(game.gameFormat?.winPoints || 0);
    } else if (tiebreakerB > tiebreakerA) {
      teamBWinPointsPreview = Number(game.gameFormat?.winPoints || 0);
    }
  } else if (game.gameFormat?.setAsSkin) {
    if (setPointPreview.teamAShots > setPointPreview.teamBShots) {
      teamAWinPointsPreview = Number(game.gameFormat?.winPoints || 0);
    } else if (setPointPreview.teamBShots > setPointPreview.teamAShots) {
      teamBWinPointsPreview = Number(game.gameFormat?.winPoints || 0);
    } else if (setPointPreview.teamAShots > 0 || setPointPreview.teamBShots > 0) {
      teamAWinPointsPreview = drawPoints;
      teamBWinPointsPreview = drawPoints;
    }
  } else if (setPointPreview.teamASetWins > setPointPreview.teamBSetWins) {
    teamAWinPointsPreview = Number(game.gameFormat?.winPoints || 0);
  } else if (setPointPreview.teamBSetWins > setPointPreview.teamASetWins) {
    teamBWinPointsPreview = Number(game.gameFormat?.winPoints || 0);
  } else if (
    setPointPreview.teamASetWins > 0 ||
    setPointPreview.teamBSetWins > 0 ||
    setPointPreview.drawnSets > 0
  ) {
    teamAWinPointsPreview = drawPoints;
    teamBWinPointsPreview = drawPoints;
  }

  const teamAGamePointsPreview =
    (includeSetPointsInGameTotals ? setPointPreview.teamASetPoints : 0) +
    teamAExtraPreview +
    teamAWinPointsPreview;
  const teamBGamePointsPreview =
    (includeSetPointsInGameTotals ? setPointPreview.teamBSetPoints : 0) +
    teamBExtraPreview +
    teamBWinPointsPreview;

  const handleExtraEndPointsChange = (team, value) => {
    const sourceName = team === 'A' ? 'extraEndA' : 'extraEndB';
    const targetName = team === 'A' ? 'extraEndB' : 'extraEndA';

    if (value === '') {
      setValue(sourceName, '', { shouldDirty: true, shouldValidate: true });
      setValue(targetName, '', { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (/\.$/.test(value) || /\.\d*0$/.test(value)) {
      setValue(sourceName, value, { shouldDirty: true, shouldValidate: false });
      return;
    }

    const numericValue = parseFloat(value);
    if (!Number.isFinite(numericValue)) {
      return;
    }

    const boundedValue = Math.max(0, Math.min(numericValue, maxExtraEndPointsTotal));
    const remaining = Math.max(maxExtraEndPointsTotal - boundedValue, 0);

    setValue(sourceName, `${boundedValue}`, { shouldDirty: true, shouldValidate: true });
    setValue(targetName, `${remaining}`, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    const playoffEnabled = game.gameFormat?.playoffs;
    if (!playoffEnabled) {
      setNeedsTiebreaker(false);
      return;
    }

    let teamAWins = 0;
    let teamBWins = 0;
    let draws = 0;

    for (let i = 0; i < totalSets; i++) {
      const scoreA = parseInt(allValues[`scoreA_${i}`], 10) || 0;
      const scoreB = parseInt(allValues[`scoreB_${i}`], 10) || 0;

      if (scoreA > scoreB) {
        teamAWins++;
      } else if (scoreB > scoreA) {
        teamBWins++;
      } else if (scoreA === scoreB && scoreA > 0) {
        draws++;
      }
    }

    const allSetsPlayed = teamAWins + teamBWins + draws === totalSets;
    const teamWinsEqual = teamAWins === teamBWins;

    setNeedsTiebreaker(allSetsPlayed && teamWinsEqual && teamAWins + teamBWins + draws > 0);
  }, [allValues, totalSets, game.gameFormat?.playoffs]);

  const handleClickOpen = () => {
    setOpen(true);
    setScorecardPhoto(null);
    setPhotoError(null);
    reset(getInitialFormValues());
  };

  const handleClose = () => {
    setOpen(false);
    reset(getInitialFormValues());
    setScorecardPhoto(null);
    setPhotoError(null);
  };

  const validateForm = () => {
    for (let i = 0; i < totalSets; i++) {
      const scoreA = allValues[`scoreA_${i}`];
      const scoreB = allValues[`scoreB_${i}`];
      if (!scoreA || scoreA === '' || !scoreB || scoreB === '') {
        alert(`Please enter both team scores for ${setLabel} ${i + 1}`);
        return false;
      }
    }

    if (needsTiebreaker) {
      const tiebreakerA = allValues.tiebreakerA;
      const tiebreakerB = allValues.tiebreakerB;
      if (!tiebreakerA || tiebreakerA === '' || !tiebreakerB || tiebreakerB === '') {
        alert('Please enter both tiebreaker scores');
        return false;
      }
    }

    if (hasExtraPoints) {
      const extraEndA = allValues.extraEndA;
      const extraEndB = allValues.extraEndB;
      const extraTotal = (parseFloat(extraEndA) || 0) + (parseFloat(extraEndB) || 0);
      if (extraEndA === '' || extraEndB === '' || extraTotal === 0) {
        alert('Please enter the extra end points');
        return false;
      }
    }

    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    setPhotoError(null);

    if (processingPhotoRef.current) return;
    if (!file) {
      setPhotoError('No file selected');
      return;
    }

    processingPhotoRef.current = true;
    setProcessingPhoto(true);

    prepareScorecardPhoto(file, { isMobile })
      .then((resizedImage) => {
        setScorecardPhoto(resizedImage);
        setPhotoError(null);
      })
      .catch((error) => {
        setPhotoError(error?.message || 'Failed to process image. Please try again.');
      })
      .finally(() => {
        processingPhotoRef.current = false;
        setProcessingPhoto(false);
      });
  };

  const handleTakePhoto = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const handleUploadPhoto = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
      uploadInputRef.current.click();
    }
  };

  const handleClearPhoto = () => {
    setScorecardPhoto(null);
    setPhotoError(null);
  };

  const onSubmit = async (data) => {
    if (processingPhoto) return;
    if (!validateForm()) return;

    const scores = [];
    for (let i = 0; i < totalSets; i++) {
      scores.push({
        set: i + 1,
        teams: {
          [teamA.id]: parseInt(data[`scoreA_${i}`], 10),
          [teamB.id]: parseInt(data[`scoreB_${i}`], 10)
        }
      });
    }

    const submitData = {
      game,
      gameId: game.id,
      scores,
      teamExtraGamePoints: {
        [teamA.id]: parseFloat(data.extraEndA || 0),
        [teamB.id]: parseFloat(data.extraEndB || 0)
      }
    };

    if (needsTiebreaker) {
      submitData.tiebreakerScore = {
        [teamA.id]: parseInt(data.tiebreakerA, 10),
        [teamB.id]: parseInt(data.tiebreakerB, 10)
      };
    }

    setSavingPhoto(true);
    try {
      if (allowPhotos && scorecardPhoto) {
        if (savePhoto) {
          await savePhoto(scorecardPhoto);
        } else {
          await dispatch(
            saveScorecardPhoto({ gameId: game.id, image: scorecardPhoto })
          ).unwrap();
        }
      }
    } catch (error) {
      // Continue score submission even if photo upload fails.
      // eslint-disable-next-line no-console
      console.error('Error saving photo:', error);
    } finally {
      setSavingPhoto(false);
    }

    try {
      if (submitScore) {
        await submitScore(submitData);
      } else {
        await dispatch(inputSetScore(submitData)).unwrap();
      }

      handleClose();
      if (onOpen) {
        onOpen();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting scores:', error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          'Error submitting scores. Please try again.'
      );
    }
  };

  const renderSummaryRow = (
    leftValue,
    label,
    rightValue,
    valueColor = 'success.main',
    labelColor = 'inherit'
  ) => (
    <Box display={'grid'} gridTemplateColumns={'1fr 1.6fr 1fr'} alignItems={'center'} columnGap={2}>
      <Typography
        variant={isMobile ? 'body1' : 'h6'}
        color={valueColor}
        fontWeight="bold"
        textAlign="center">
        {leftValue}
      </Typography>
      <Typography
        variant={isMobile ? 'body2' : 'body1'}
        fontWeight="bold"
        textAlign="center"
        color={labelColor}>
        {label}
      </Typography>
      <Typography
        variant={isMobile ? 'body1' : 'h6'}
        color={valueColor}
        fontWeight="bold"
        textAlign="center">
        {rightValue}
      </Typography>
    </Box>
  );

  return (
    <>
      <Button
        variant="contained"
        color={triggerColor}
        onClick={handleClickOpen}
        fullWidth={isMobile}
        sx={triggerSx}>
        Input Score
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} component="div">
              Score Game
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {game.name ?? 'Private Game'}
            </Typography>
          </Box>
        </DialogTitle>

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Box
          component="form"
          onSubmit={rhfHandleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0
          }}>
          <DialogContent
            sx={{
              px: isMobile ? 1.5 : 3,
              pt: isMobile ? 2 : 4,
              pb: isMobile ? 3 : 4,
              overflowY: 'auto'
            }}>
            <Stack spacing={isMobile ? 2 : 3}>
              {Array.from({ length: totalSets }).map((_, i) => (
                <Box
                  key={`set-${i}`}
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1
                  }}>
                  {totalSets > 1 && (
                    <Typography
                      variant={isMobile ? 'body2' : 'body1'}
                      fontWeight="bold"
                      sx={{ mb: isMobile ? 1 : 1.5, color: 'primary.main', textAlign: 'center' }}>
                      {setLabel} {i + 1}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? 1 : 2
                    }}>
                    <ScoreInput
                      control={control}
                      name={`scoreA_${i}`}
                      teamName={teamA.name}
                      isMobile={isMobile}
                    />
                    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                      <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        fontWeight="bold"
                        sx={{ whiteSpace: 'nowrap' }}>
                        VS
                      </Typography>
                    </Box>
                    <ScoreInput
                      control={control}
                      name={`scoreB_${i}`}
                      teamName={teamB.name}
                      isMobile={isMobile}
                    />
                  </Box>
                </Box>
              ))}

              {hasExtraPoints && (
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    border: '2px solid',
                    borderColor: 'success.main',
                    borderRadius: 1
                  }}>
                  <Typography
                    variant={isMobile ? 'body2' : 'body1'}
                    fontWeight="bold"
                    sx={{ mb: 1, color: 'success.main' }}>
                    Extra End Points
                  </Typography>
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ mb: isMobile ? 1.5 : 2, display: 'block' }}>
                    Auto-fill enabled: total between both teams must equal {maxExtraEndPointsTotal}.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? 1 : 2
                    }}>
                    <ScoreInput
                      control={control}
                      name="extraEndA"
                      teamName={teamA.name}
                      isMobile={isMobile}
                      compact
                      allowDecimal
                      maxValue={maxExtraEndPointsTotal}
                      onValueChange={(value) => handleExtraEndPointsChange('A', value)}
                    />
                    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                      <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        fontWeight="bold"
                        sx={{ whiteSpace: 'nowrap' }}>
                        VS
                      </Typography>
                    </Box>
                    <ScoreInput
                      control={control}
                      name="extraEndB"
                      teamName={teamB.name}
                      isMobile={isMobile}
                      compact
                      allowDecimal
                      maxValue={maxExtraEndPointsTotal}
                      onValueChange={(value) => handleExtraEndPointsChange('B', value)}
                    />
                  </Box>
                </Box>
              )}

              {needsTiebreaker && (
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    border: '2px solid',
                    borderColor: 'warning.main',
                    borderRadius: 1
                  }}>
                  <Typography
                    variant={isMobile ? 'body2' : 'body1'}
                    fontWeight="bold"
                    sx={{ mb: 1, color: 'warning.dark' }}>
                    ⚠ Tiebreaker
                  </Typography>
                  <Typography
                    variant="caption"
                    color="warning.dark"
                    sx={{ mb: isMobile ? 1.5 : 2, display: 'block' }}>
                    {setLabelPlural} are tied - Enter tiebreaker score
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? 1 : 2
                    }}>
                    <ScoreInput
                      control={control}
                      name="tiebreakerA"
                      teamName={teamA.name}
                      isMobile={isMobile}
                    />
                    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                      <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        fontWeight="bold"
                        sx={{ whiteSpace: 'nowrap' }}>
                        VS
                      </Typography>
                    </Box>
                    <ScoreInput
                      control={control}
                      name="tiebreakerB"
                      teamName={teamB.name}
                      isMobile={isMobile}
                    />
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 1
                }}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  fontWeight="bold"
                  sx={{ mb: 1.5, color: 'success.main', textAlign: 'center' }}>
                  Dynamic Game Point Summary
                </Typography>

                <Stack spacing={1.25}>
                  {game.gameFormat?.setAsSkin &&
                    renderSummaryRow(
                      setPointPreview.teamAShots,
                      `${setLabelPlural} Totals Accumulated`,
                      setPointPreview.teamBShots,
                      'primary.main',
                      'primary.main'
                    )}
                  {totalSets > 1 &&
                    renderSummaryRow(
                      setPointPreview.teamASetPoints,
                      `Total ${setLabel} Points`,
                      setPointPreview.teamBSetPoints
                    )}
                  {hasExtraPoints &&
                    renderSummaryRow(teamAExtraPreview, 'Extra End Points', teamBExtraPreview)}
                  {renderSummaryRow(teamAWinPointsPreview, 'Win Points', teamBWinPointsPreview)}
                  {renderSummaryRow(
                    teamAGamePointsPreview,
                    'Total Game Points Overall',
                    teamBGamePointsPreview
                  )}
                </Stack>
              </Box>

              {allowPhotos && (
              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  border: '2px solid',
                  borderColor: 'info.main',
                  borderRadius: 1
                }}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  fontWeight="bold"
                  sx={{ mb: 1, color: 'info.dark' }}>
                  📸 Scoreboard Photo
                </Typography>

                {photoError && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {photoError}
                  </Alert>
                )}

                {processingPhoto && (
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Preparing photo...
                    </Typography>
                  </Box>
                )}

                {scorecardPhoto ? (
                  <Box>
                    <Box
                      component="img"
                      src={scorecardPhoto}
                      alt="Scoreboard"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: isMobile ? 150 : 200,
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'info.main',
                        mb: 1.5
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleClearPhoto}
                        disabled={processingPhoto}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}>
                        Remove
                      </Button>
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={handleUploadPhoto}
                        disabled={processingPhoto}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}>
                        Change
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<CameraAlt />}
                      onClick={handleTakePhoto}
                      disabled={processingPhoto}
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}>
                      Take Photo
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      startIcon={<Upload />}
                      onClick={handleUploadPhoto}
                      disabled={processingPhoto}
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}>
                      Upload Photo
                    </Button>
                  </Stack>
                )}
              </Box>
              )}
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: isMobile ? 1 : 2,
              pt: isMobile ? 1 : 2,
              pb: isMobile ? 'max(8px, env(safe-area-inset-bottom))' : 2,
              flexDirection: isMobile ? 'column-reverse' : 'row',
              gap: isMobile ? 1 : 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper'
            }}>
            <Button
              onClick={handleClose}
              color="primary"
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={savingPhoto || busy || processingPhoto}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'large'}
              sx={{ minHeight: 48, fontWeight: 700 }}>
              {busy ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default InputScoreDialog;
